/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import { userHasOneOfTheseRoles } from "../../../../services/backofficeUserService";
import { Roles } from "../../../../types/BackofficeUser";
import { useCurrentUser } from "../../../../provider/CurrentUserProvider";
import ContentBox from "../../../ContentBox/ContentBox";
import { Box, ListSubheader, SelectChangeEvent, Stack, Tooltip, Typography } from "@mui/material";
import ButtonLoading from "../../../Button/ButtonLoading";
import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";
import ApiClient from "../../../../services/ApiClient";
import Grid from "@mui/material/Grid";
import ValidatorTextField from "../../../Validator/ValidatorTextField";
import ValidatorSelect from "../../../Validator/ValidatorSelect";
import MenuItem from "@mui/material/MenuItem";
import SearchBar from "../../../Algolia/SearchBar";
import { ALGOLIA_INDEX } from "../../../../config/_entrypoint";
import CaseHit from "../../../Case/CaseEntityPicker/Hit/CaseHit";
import { CaseHit as CaseHitType } from "../../../Case/CaseEntityPicker/Hit/hitType";
import { FormContainer, useForm } from "react-hook-form-mui";
import { useQuery } from "@tanstack/react-query";
import { PromptEditor } from "../../../PromptEditor/PromptEditor";
import { aiLabPromptForgeStyles } from "./AiLabPromptForgeStyles";
import { AiDocumentsSelect } from "../../../AiDocumentsSelect/AiDocumentsSelect";
import { AiEventStreamProvider } from "../../../../provider/AiEventStreamProvider";
import { useAiEventStreamControls } from "../../../../hooks/useAiEventStream";
import { PromptGenerationStreamOutputWithRandomEmptyText } from "../../../AiEventStream/PromptGenerationStreamOutputWithRandomEmptyText";

const emptyPrompt = `---
model: gemini-2.5-flash
temperature: 0
max_tokens: 5000
---
@@@ system
Du bist ein KI-Modell, das in der Lage ist, auf Anfragen zu antworten und Texte zu generieren. Deine Aufgabe ist es, auf die vom User gestellte Frage bestmöglich zu antworten.
@@@ user
{{ additionalContext }}`;

type AvailablePrompt = {
  promptName: string;
  displayName: string;
};

type PromptForgeFormValues = {
  documents: number[];
};

type PromptForgeProduct = {
  id: number;
  type: string;
};

type PromptPartialsResponse = {
  partials?: string[];
  macros?: Array<{
    template: string;
    description: string;
    macros: Array<{
      name: string;
      description: string;
      args: string[];
    }>;
  }>;
};

type PartialDefinition = {
  path: string;
  description: string;
};

type MacroDefinition = {
  template: string;
  description: string;
  macros: Array<{
    name: string;
    description: string;
    args: string[];
  }>;
};

type PromptDefinitions = {
  partialDefinitions: PartialDefinition[];
  macroDefinitions: MacroDefinition[];
};

function AiLabPromptForgeContent() {
  const [isLoading, setIsLoading] = useState(false);
  const [prompt, setPrompt] = useState("empty");
  const [model, setModel] = useState<string>("default");
  const [promptContent, setPromptContent] = useState(emptyPrompt);
  const [product, setProduct] = useState<PromptForgeProduct | null>(null);
  const [context, setContext] = useState<Record<string, string>>({});
  const [placeholder, setPlaceholder] = useState<string[]>([]);
  const [serializationContext, setSerializationContext] = useState("serviceworld");
  const [generatedText, setGeneratedText] = useState("");
  const [generatedPrompt, setGeneratedPrompt] = useState("");
  const [availablePrompts, setAvailablePrompts] = useState<AvailablePrompt[]>([]);
  const [sendPromptError, setSendPromptError] = useState<string | null>(null);

  const { connect, reset } = useAiEventStreamControls();

  const formContext = useForm<PromptForgeFormValues>({
    mode: "onBlur",
    defaultValues: {
      documents: [],
    },
  });

  const { data: promptDefinitionsData } = useQuery({
    queryKey: ["ai-lab", "prompt-partials"],

    queryFn: async (): Promise<PromptPartialsResponse> => {
      return await ApiClient.get("/lb/services/ai_lab/get_partials");
    },

    select: (response): PromptDefinitions => {
      const partials = Array.isArray(response?.partials) ? response.partials : [];
      const macros = Array.isArray(response?.macros) ? response.macros : [];

      const partialDefinitions = [...new Set(partials)]
        .filter((path): path is string => path.trim().length > 0)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
        .map((path) => ({
          path,
          description: path,
        }));

      const macroDefinitions = macros
        .filter(
          (macroTemplate): macroTemplate is MacroDefinition =>
            typeof macroTemplate?.template === "string" &&
            macroTemplate.template.trim().length > 0 &&
            Array.isArray(macroTemplate.macros)
        )
        .sort((a, b) => a.template.localeCompare(b.template, undefined, { sensitivity: "base" }));

      return {
        partialDefinitions,
        macroDefinitions,
      };
    }
  });

  const partialDefinitions = promptDefinitionsData?.partialDefinitions ?? [];
  const macroDefinitions = promptDefinitionsData?.macroDefinitions ?? [];

  const jinjaCatalog = useMemo(
    () => ({
      partials: partialDefinitions,
      macroTemplates: macroDefinitions,
    }),
    [partialDefinitions, macroDefinitions]
  );

  const reservedPlaceholders = ["caseData"];

  const cutAtTopLevelPipe = (value: string): string => {
    let inSingle = false;
    let inDouble = false;
    let escape = false;
    let paren = 0;
    let bracket = 0;
    let brace = 0;

    for (let i = 0; i < value.length; i++) {
      const char = value[i];

      if (escape) {
        escape = false;
        continue;
      }

      if (char === "\\" && (inSingle || inDouble)) {
        escape = true;
        continue;
      }

      if (!inDouble && char === "'" && !inSingle) {
        inSingle = true;
      } else if (inSingle && char === "'") {
        inSingle = false;
      } else if (!inSingle && char === '"' && !inDouble) {
        inDouble = true;
      } else if (inDouble && char === '"') {
        inDouble = false;
      } else if (!inSingle && !inDouble) {
        if (char === "(") {
          paren++;
        } else if (char === ")") {
          paren = Math.max(0, paren - 1);
        } else if (char === "[") {
          bracket++;
        } else if (char === "]") {
          bracket = Math.max(0, bracket - 1);
        } else if (char === "{") {
          brace++;
        } else if (char === "}") {
          brace = Math.max(0, brace - 1);
        } else if (char === "|" && paren === 0 && bracket === 0 && brace === 0) {
          return value.slice(0, i);
        }
      }
    }

    return value;
  };

  const extractTopLevelPlaceholders = (template: string): string[] => {
    const results = new Set<string>();
    const mustache = /{{-?\s*([\s\S]*?)\s*-?}}/g;
    let match: RegExpExecArray | null;

    while ((match = mustache.exec(template)) !== null) {
      const candidate = cutAtTopLevelPipe(match[1]).trim();

      if (/[.\[\]()"'\s]/.test(candidate) && !/^[A-Za-z_][A-Za-z0-9_]*$/.test(candidate)) {
        continue;
      }

      if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(candidate) && !reservedPlaceholders.includes(candidate)) {
        results.add(candidate);
      }
    }

    return Array.from(results);
  };

  const handleContextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setContext((previous) => ({ ...previous, [name]: value }));
  };

  useEffect(() => {
    const fetchData = async () => {
      const response = await ApiClient.get(`/lb/services/ai_lab/get_prompts`);
      setAvailablePrompts([...response.prompts].sort());
    };

    fetchData();
  }, []);

  useEffect(() => {
    const names = extractTopLevelPlaceholders(promptContent).sort();

    setPlaceholder((previous) => {
      const unchanged = previous.length === names.length && previous.every((name, index) => name === names[index]);

      if (!unchanged) {
        const preservedContext: Record<string, string> = {};
        for (const name of names) {
          if (name in context) {
            preservedContext[name] = context[name];
          }
        }
        setContext(preservedContext);
      }

      return names;
    });
  }, [promptContent]);

  useEffect(() => {
    const fetchData = async () => {
      if (prompt === "empty") {
        setPromptContent(emptyPrompt);
        return;
      }

      try {
        const response = await ApiClient.get(`/lb/services/ai_lab/get_prompt/${prompt}`);
        setPromptContent(response.content);
      } catch (error) {
        setPromptContent("Fehler beim Laden des Prompts");
      }
    };

    fetchData();
  }, [prompt]);

  const sendPrompt = async () => {
    setIsLoading(true);
    setGeneratedText("");
    setGeneratedPrompt("");
    setSendPromptError(null);

    const formValues = formContext.getValues();
    const streamId = connect();

    try {
      const response = await ApiClient.post(`/lb/services/ai_lab/send_custom_prompt`, {
        body: JSON.stringify({
          productId: product?.id || 0,
          prompt: promptContent,
          context: context,
          serializationContext: serializationContext,
          forceModel: model !== "default" ? model : undefined,
          documents: formValues?.documents || undefined,
          streamId: streamId,
        }),
      });

      setGeneratedText(response["generatedText"] || "");

      let promptString = "";
      const promptResponse = response["prompt"] ?? {};

      Object.entries(promptResponse).forEach(([key, value]) => {
        promptString += `${key}:\n-----------------\n${value}\n\n`;
      });

      const meta = response["meta"] ?? {};
      promptString += "\n------------------------\nRESPONSE METADATA\n------------------------\n";
      promptString += `Provider: ${meta["provider"] ?? ""}\n`;
      promptString += `Model: ${meta["model"] ?? ""}\n`;

      setGeneratedPrompt(promptString);
    } catch (error) {
      reset();
      setSendPromptError("Der Prompt konnte nicht abgeschickt werden.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePromptChange = async (event: SelectChangeEvent) => {
    setPrompt(event.target.value);
  };

  const onHitClick = async ({ product, productId }: CaseHitType, event?: SyntheticEvent<HTMLElement>) => {
    if (!event) {
      return;
    }

    event.preventDefault();

    setProduct({
      type: product,
      id: productId,
    });
  };

  return (
    <ContentBox headline="🔧🦾 Prompt-Schmiede">
      <FormContainer formContext={formContext}>
        <Grid container spacing={1} sx={{
          alignItems: "center"
        }}>
          <Grid size={12}>
            <Typography>
              Hier können Prompts für die KI-Modelle erstellt und getestet werden. Die Prompts werden 1 zu 1 an den AI
              Service weitergegeben und können nach dem testen an einen Dev mit Zugriff auf das AI System gesendet
              werden um sie in Betrieb zu nehmen.
            </Typography>
          </Grid>

          <Grid size={3}>
            <ValidatorSelect
              label="Prompts"
              value={prompt}
              onChange={handlePromptChange}
              helperText="Auswahl an Prompts die auf der verbundenen AI Api zur Verfügung stehen."
            >
              <MenuItem value="empty">Leerer Prompt</MenuItem>

              {availablePrompts.map(({ promptName, displayName }) => (
                <MenuItem key={promptName} value={promptName}>
                  <Tooltip title={displayName} placement="right">
                    <span>{promptName}</span>
                  </Tooltip>
                </MenuItem>
              ))}
            </ValidatorSelect>
          </Grid>

          <Grid size={3}>
            <ValidatorSelect
              label="Modell"
              value={model}
              onChange={(event: SelectChangeEvent) => setModel(event.target.value as string)}
              helperText={"Auswahl des Modells mit welchem der Prompt ausgeführt werden soll."}
            >
              <MenuItem value="default">Wie im Prompt definiert</MenuItem>
              <ListSubheader disableSticky sx={{ fontWeight: "bold" }}>
                Test- und Produktivumgebung
              </ListSubheader>
              <MenuItem value="gemini-2.5-pro">gemini-2.5-pro</MenuItem>
              <MenuItem value="gemini-2.5-flash">gemini-2.5-flash</MenuItem>
              <MenuItem value="gemini-2.5-flash-lite">gemini-2.5-flash-lite</MenuItem>
              <MenuItem value="gemini-2.0-flash">gemini-2.0-flash</MenuItem>
              <MenuItem value="gemini-2.0-flash-lite">gemini-2.0-flash-lite</MenuItem>
              <MenuItem value="gpt-5">gpt-5</MenuItem>
              <MenuItem value="gpt-5-mini">gpt-5-mini</MenuItem>
              <MenuItem value="gpt-5-nano">gpt-5-nano</MenuItem>
              <MenuItem value="gpt-4.1">gpt-4.1</MenuItem>
              <MenuItem value="gpt-4o-mini">gpt-4o-mini</MenuItem>
              <MenuItem value="gpt-o4">gpt-o4</MenuItem>
            </ValidatorSelect>
          </Grid>

          <Grid size={3}>
            <ValidatorSelect
              label="Ausführungsumgebung"
              value={serializationContext}
              onChange={(event) => {
                setSerializationContext(event.target.value);
              }}
              helperText={"Gibt an ob der Prompt aus der Servicewelt oder dem Backoffice aufgerufen werden soll."}
            >
              <MenuItem value={"serviceworld"}>Servicewelt</MenuItem>
              <MenuItem value={"backoffice"}>Backoffice</MenuItem>
            </ValidatorSelect>
          </Grid>

          <Grid sx={{ height: "100%" }} size={3}>
            <Stack
              spacing={2}
              sx={{
                justifyContent: "center",
                height: "100%"
              }}>
              <SearchBar
                searchConfig={{
                  indexName: ALGOLIA_INDEX,
                }}
                resultListConfig={{
                  itemSize: 330,
                  width: "100%",
                }}
                HitComponent={CaseHit}
                onHitClick={onHitClick}
              />
              <Typography>
                Ausgewählter Case: <strong>{product?.id || "Kein Case ausgewählt"}</strong>
              </Typography>
            </Stack>
          </Grid>

          <Grid size={12}>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Prompt</Typography>

              <Box sx={aiLabPromptForgeStyles.editorContainer}>
                <PromptEditor
                  value={promptContent}
                  onChange={setPromptContent}
                  catalog={jinjaCatalog}
                  height={640}
                  theme="light"
                />
              </Box>
            </Stack>
          </Grid>

          <Grid sx={{ mt: 1.5, alignSelf: "flex-start" }} size={12}>
            <AiDocumentsSelect product={product} />
          </Grid>

          {placeholder.map((name) => (
            <Grid key={name} size={12}>
              <ValidatorTextField
                label={name}
                name={name}
                value={context[name] ?? ""}
                onChange={handleContextChange}
                multiline
                rows={4}
              />
            </Grid>
          ))}

          <Grid
            sx={{
              py: 3
            }}
            size={12}>
            <Stack spacing={1} sx={{
              alignItems: "flex-start"
            }}>
              <ButtonLoading variant={"contained"} onClick={sendPrompt} isLoading={isLoading}>
                Prompt senden
              </ButtonLoading>

              {sendPromptError && (
                <Typography variant="body2" color="error">
                  {sendPromptError}
                </Typography>
              )}
            </Stack>
          </Grid>

          <Grid size={12}>
            <Typography variant="body1">
              <strong>Streaming-Test:</strong>
            </Typography>
            <PromptGenerationStreamOutputWithRandomEmptyText sx={[aiLabPromptForgeStyles.streamingContainer(220)]} />
          </Grid>

          {generatedText && (
            <>
              <Grid size={12}>
                <Typography variant="body1">
                  <strong>Generierter Text:</strong>
                </Typography>
                <Box
                  sx={{
                    height: 300,
                    overflowY: "auto",
                    border: "1px solid",
                    borderColor: "grey.400",
                    p: 1,
                  }}
                >
                  <div dangerouslySetInnerHTML={{ __html: generatedText }} />
                </Box>
              </Grid>

              <Grid size={12}>
                <ValidatorTextField
                  label={"Erstellter Prompt"}
                  name={"generatedPrompt"}
                  value={generatedPrompt}
                  multiline
                  rows={8}
                />
              </Grid>
            </>
          )}
        </Grid>
      </FormContainer>
    </ContentBox>
  );
}

export default function AiLabPromptForge() {
  const currentUser = useCurrentUser();

  if (!userHasOneOfTheseRoles(currentUser, [Roles.admin, Roles.developer, Roles.productManager])) {
    return <></>;
  }

  return (
    <AiEventStreamProvider>
      <AiLabPromptForgeContent />
    </AiEventStreamProvider>
  );
}
